/**
 * GameMasterPanel — view do papel master.
 *
 * Mostra o slide corrente idêntico ao do player (mestre vê o que eles veem)
 * + controles flutuantes (MasterControlsPill) + dock lateral opcional com
 * painel específico da missão (registrado em componentRegistry).
 */

import { useEffect, useRef, useState } from 'react'

import { MasterActivityControls } from '../components/MasterActivityControls'
import { MasterControlsPill } from '../components/MasterControlsPill'
import { QrJoinOverlay } from '../components/QrJoinOverlay'
import { ScoreBoard } from '../components/ScoreBoard'
import { SlideShell } from '../components/SlideShell'
import { useSessionState } from '../hooks/useSessionState'
import { useSession } from '../session'
import type { Manifest } from '../types/manifest'
import { COMPONENT_REGISTRY } from '../registry/componentRegistry'
import { SlideRouter } from './SlideRouter'

interface Props {
  manifest: Manifest
}

export function GameMasterPanel({ manifest }: Props) {
  const session = useSession()
  const { slideIndex, membershipId, snapshotReceived } = useSessionState()
  const slide = manifest.slides[slideIndex]
  const label = slide?.label
  const isMission = slide?.type === 'mission'
  // custom (viz interativa) também ocupa a tela inteira, como mission.
  const isFullbleed = isMission || slide?.type === 'custom'

  // QR de entrada: abre sozinho quando a aula ainda está no slide 0 (antes
  // de começar a apresentar); reconexão no meio da aula não reabre. Estado
  // local do mestre — não propaga aos alunos.
  const [showQr, setShowQr] = useState(false)
  const autoShownQr = useRef(false)
  useEffect(() => {
    if (snapshotReceived && !autoShownQr.current) {
      autoShownQr.current = true
      if (slideIndex === 0) setShowQr(true)
    }
  }, [snapshotReceived, slideIndex])

  // Slot opcional do painel-do-mestre por missão. Em PR-1 nenhuma missão
  // tem masterPanel — o dock simplesmente não renderiza. PR-2 popula para
  // os componentes lab.*.
  const MasterPanel = isMission && slide?.type === 'mission'
    ? COMPONENT_REGISTRY.resolve(slide.missionId)?.masterPanel
    : null

  return (
    <>
      <SlideShell variant={isFullbleed ? 'fullbleed' : 'default'}>
        <SlideRouter slide={slide} />
      </SlideShell>
      <MasterControlsPill
        totalSlides={manifest.slides.length}
        currentIndex={slideIndex}
        currentLabel={label}
        onShowQr={() => setShowQr(true)}
      />
      {showQr && (
        <QrJoinOverlay tableId={session.tableId} onClose={() => setShowQr(false)} />
      )}
      {isMission && <MasterActivityControls slide={slide} />}
      {MasterPanel && (
        <aside
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            width: 360,
            background: 'var(--color-lab-bg-2, #F2F2ED)',
            borderLeft: '1px solid var(--color-lab-rule, #D8D5CB)',
            overflowY: 'auto',
            zIndex: 30,
            padding: 'var(--spacing-lab-4, 16px)',
          }}
        >
          <MasterPanel
            membershipId={membershipId}
            slideIndex={slideIndex}
            slideConfig={slide as unknown as Record<string, unknown>}
          />
        </aside>
      )}
      <ScoreBoard />
    </>
  )
}
